import React, { useState, useEffect } from 'react';
import ParallaxHero from '../components/ParallaxHero';

interface Review {
  name: string;
  content: string;
  date: string;
}

const RealStoriesPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('frietplus_reviews');
    if (stored) {
      setReviews(JSON.parse(stored));
    }
  }, []);

  type FormStatus = 'idle' | 'loading' | 'success' | 'error';
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');

  const setDutchValidity = (e: React.FormEvent<any>) => {
    const el = e.target as HTMLInputElement;
    if (el.validity.valueMissing) el.setCustomValidity('Dit veld is verplicht.');
    else el.setCustomValidity('');
  };
  const clearValidity = (e: React.ChangeEvent<any>) => {
    (e.target as HTMLInputElement).setCustomValidity('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;
    setFormStatus('loading');
    try {
      const formData = new FormData(e.currentTarget);
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      if (res.ok) {
        const newReview: Review = {
          name: name.trim(),
          content: content.trim(),
          date: new Date().toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' }),
        };
        const updated = [newReview, ...reviews];
        setReviews(updated);
        localStorage.setItem('frietplus_reviews', JSON.stringify(updated));
        setName('');
        setContent('');
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div>
      <ParallaxHero
        image="/assets/gallery/514541036_17867754138406413_109138853678114058_n.jpg"
        title="Real Stories"
        subtitle="- Echte verhalen van echte klanten -"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">

            <p className="font-serif text-lg text-gray-600 max-w-2xl mx-auto">
              Ontdek waarom zoveel mensen kiezen voor FrietPlus op hun speciale dag.
            </p>
            <div className="w-full max-w-4xl h-[2px] bg-friet-red mx-auto mt-6"></div>
          </div>

          {reviews.length > 0 && (
            <div className="grid md:grid-cols-2 gap-12">
              {reviews.map((review, i) => (
                <div key={i} className="bg-friet-beige/10 rounded-3xl p-8 md:p-12 relative">
                  <div className="absolute top-8 left-8 text-6xl text-friet-gold/10 font-serif">"</div>
                  <div className="relative z-10">
                    <p className="text-xl italic text-gray-700 mb-8 leading-relaxed">
                      "{review.content}"
                    </p>
                    <div className="flex items-center gap-4 border-t border-friet-gold/20 pt-8">
                      <div className="w-16 h-16 bg-friet-gold rounded-full flex items-center justify-center text-white font-serif text-2xl">
                        {review.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-serif text-xl font-bold">{review.name}</h4>
                        <p className="text-gray-500 text-sm">{review.date}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-friet-beige">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-friet-oak">Laat een <span className="font-script text-friet-red">Recensie</span> achter</h2>
            <div className="w-16 h-[1px] bg-friet-oak/30 mx-auto mt-5 mb-5"></div>
            <p className="text-friet-oak/60 text-sm font-serif italic leading-relaxed max-w-lg mx-auto">
              Heb je FrietPlus al ervaren op jouw event? We horen graag hoe het was!<br />
              Deel je verhaal en help anderen bij hun keuze.
            </p>
          </div>

          {formStatus === 'success' && (
            <div className="max-w-2xl mx-auto mb-8 bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg font-serif text-sm flex items-start gap-3">
              <i className="fas fa-check-circle mt-0.5 text-green-600"></i>
              <div><strong>Bedankt!</strong> Je recensie is succesvol verzonden en zichtbaar op de pagina.</div>
            </div>
          )}
          {formStatus === 'error' && (
            <div className="max-w-2xl mx-auto mb-8 bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg font-serif text-sm flex items-start gap-3">
              <i className="fas fa-exclamation-circle mt-0.5 text-red-500"></i>
              <div><strong>Er ging iets mis.</strong> Probeer het opnieuw of stuur een e-mail naar <a href="mailto:info@frietplus.com" className="underline">info@frietplus.com</a>.</div>
            </div>
          )}

          <form className="max-w-2xl mx-auto" name="recensie" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit}>
            <input type="hidden" name="form-name" value="recensie" />
            <div className="mb-5">
              <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Naam <span className="text-friet-red">*</span></label>
              <input
                name="naam"
                type="text"
                required
                value={name}
                onInvalid={setDutchValidity}
                onChange={(e) => { clearValidity(e); setName(e.target.value); }}
                className="w-full bg-white border border-friet-oak/20 outline-none py-2.5 px-3 text-sm text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors"
                placeholder="Jouw naam"
              />
            </div>
            <div>
              <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Jouw ervaring <span className="text-friet-red">*</span></label>
              <textarea
                name="bericht"
                rows={8}
                required
                value={content}
                onInvalid={setDutchValidity}
                onChange={(e) => { clearValidity(e); setContent(e.target.value); }}
                className="w-full bg-white border border-friet-oak/20 outline-none py-2.5 px-3 text-sm text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors resize-none"
                placeholder="Vertel ons over je ervaring met FrietPlus. Wat vond je het leukste? Hoe reageerden je gasten?"
              ></textarea>
            </div>

            <div className="mt-5 text-center">
              <button type="submit" disabled={formStatus === 'loading'} className="bg-friet-red text-white px-12 py-3.5 font-bold text-sm tracking-wider uppercase hover:bg-friet-black transition-all disabled:opacity-60">
                {formStatus === 'loading' ? 'Bezig met verzenden...' : 'Recensie Verzenden'}
              </button>
            </div>
          </form>
        </div>
      </section>

    </div>
  );
};

export default RealStoriesPage;
